// src/layout/customer/RejectedProject/RejectionCard.jsx
import React, { useEffect } from "react";
import { BadgeX, Calendar, Info } from "lucide-react"; // Added Info icon
import useProjectStatus from "../../../store/builders/useProjectStatus"; // Using builder store for project data
import { useNavigate } from "react-router-dom";
import { Tooltip } from 'react-tooltip'; // Example using react-tooltip
import 'react-tooltip/dist/react-tooltip.css';

const RejectionCard = () => {
  const { projects, fetchProjects, isLoading, error } = useProjectStatus();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects("rejected");
  }, [fetchProjects]);

  if (error) {
    return <p className="text-center text-red-500 text-sm">{error}</p>;
  }

  if (isLoading) {
    return <p className="text-center text-gray-500 text-sm">Loading projects...</p>;
  }

  if (projects.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600 text-sm">
          Great news! You currently have no rejected projects.
        </p>
      </div>
    );
  }

  const getRejectionTypeText = (type) => {
     if (type === 'invitation') return 'Invitation Declined';
     if (type === 'live') return 'Rejected After Start';
     return 'Rejection Type Unknown';
  };

  return (
    <div className="container mx-auto px-2 sm:px-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"> {/* Adjusted gap */}
        {projects.map((project) => (
          <div key={project.id} className="bg-white rounded-xl shadow-md p-4 relative w-full mx-auto flex flex-col">
            {/* Header */}
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                <img
                  src={project.builder_profile_image || "/assets/DUCKTAIL-HOMELOAN.aviF"} // Use actual builder image if available
                  alt="Builder"
                  className="w-full h-full object-cover"
                   onError={(e) => { e.target.src = "/assets/DUCKTAIL-HOMELOAN.aviF"; }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-semibold text-gray-900 truncate" title={project.builder_company}>
                  {project.builder_company || "Unknown Builder"}
                </h2>
                <p className="text-xs text-gray-500 truncate" title={project.project_name}>
                  Project: {project.project_name || "N/A"}
                </p>
                 <p className="text-xs text-gray-500 truncate mt-0.5" title={project.project_location}>
                  Location: {project.project_location || "N/A"}
                </p>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
              {/* Start Date */}
              <div className="flex items-start gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-gray-500 block">Start Date</span>
                  <span className="text-gray-700 font-medium">
                    {project.starting_date ? new Date(project.starting_date).toLocaleDateString() : "N/A"}
                  </span>
                </div>
              </div>

              {/* Status & Type */}
               <div className="flex items-start gap-1.5">
                 <BadgeX className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                 <div>
                   <span className="text-gray-500 block">Status</span>
                   <span className="text-red-600 font-medium capitalize">
                     {project.status || "Rejected"}
                   </span>
                   {project.rejection_type && (
                      <span className="block text-[10px] text-gray-400 mt-0.5" data-tooltip-id={`tooltip-${project.id}`} data-tooltip-content={getRejectionTypeText(project.rejection_type)}>
                         ({getRejectionTypeText(project.rejection_type)})
                      </span>
                   )}
                 </div>
               </div>
            </div>

            {/* Rejection Reason */}
            {project.rejection_reason && (
               <div className="mt-2 pt-2 border-t border-gray-100 bg-red-50 p-2 rounded-md">
                  <p className="text-xs font-medium text-red-700 mb-1 flex items-center gap-1">
                     <Info className="w-3.5 h-3.5"/> Reason for Rejection:
                  </p>
                  <p className="text-xs text-red-900 whitespace-pre-wrap">{project.rejection_reason}</p>
               </div>
            )}
             {!project.rejection_reason && project.rejection_type === 'invitation' && (
                 <div className="mt-2 pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-400 italic text-center">Invitation declined.</p>
                 </div>
             )}

            {/* Tooltips (place once outside the map) */}
            <Tooltip id={`tooltip-${project.id}`} place="top" effect="solid" className="!text-xs !bg-gray-700" />

             {/* Optional: Add a spacer or ensure content pushes this down */}
            <div className="flex-grow"></div>

            {/* You might want a button to go back or contact support */}
             {/* <div className="mt-4 text-center">
                <button onClick={() => navigate('/customer/help')} className="text-xs text-blue-600 hover:underline">Contact Support</button>
            </div> */}
          </div>
        ))}
      </div>
      <button
        className="fixed bottom-3 right-3 w-12 h-12 sm:w-14 sm:h-14 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors shadow-lg"
        onClick={() => navigate("/customer/help")}
      >
        <img src="/assets/Support.png" alt="Icon" className="w-7 h-7 sm:w-9 sm:h-9" />
      </button>
    </div>
  );
};

export default RejectionCard;